import { ThemeType } from '@app/interfaces/interfaces';
import { createSlice, createAction, PrepareAction } from '@reduxjs/toolkit';

export type PanelThemeMode = ThemeType | 'custom';
export type PanelInterfaceStyle = 'simple' | 'modern';

export interface PanelThemeColors {
  primary: string;
  background: string;
  surface: string;
  text: string;
  sidebar: string;
}

interface ThemeState {
  theme: ThemeType;
  mode: PanelThemeMode;
  interfaceStyle: PanelInterfaceStyle;
  customColors: PanelThemeColors;
}

const appThemeStorageKey = 'theme';
const uiThemeStorageKey = 'vite-ui-theme';
const panelThemeModeStorageKey = 'hsdesk-panel-theme-mode';
const panelThemeColorsStorageKey = 'hsdesk-panel-theme-colors';
const panelInterfaceStyleStorageKey = 'hsdesk-panel-interface-style';

export const defaultPanelThemeColors: PanelThemeColors = {
  primary: '#4F46E5',
  background: '#F4F7FB',
  surface: '#FFFFFF',
  text: '#172033',
  sidebar: '#111827'
};

function isThemeType(value: string | null): value is ThemeType {
  return value === 'light' || value === 'dark';
}

function isPanelThemeMode(value: string | null): value is PanelThemeMode {
  return isThemeType(value) || value === 'custom';
}

function isPanelInterfaceStyle(
  value: string | null
): value is PanelInterfaceStyle {
  return value === 'simple' || value === 'modern';
}

function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);
}

function getStoredCustomColors(): PanelThemeColors {
  try {
    const stored = JSON.parse(
      localStorage.getItem(panelThemeColorsStorageKey) ?? '{}'
    ) as Partial<PanelThemeColors>;

    return Object.fromEntries(
      Object.entries(defaultPanelThemeColors).map(([key, fallback]) => [
        key,
        isHexColor(stored[key as keyof PanelThemeColors])
          ? stored[key as keyof PanelThemeColors]
          : fallback
      ])
    ) as unknown as PanelThemeColors;
  } catch {
    return defaultPanelThemeColors;
  }
}

function getReadableTheme(background: string): ThemeType {
  const [red, green, blue] = [1, 3, 5].map((offset) =>
    Number.parseInt(background.slice(offset, offset + 2), 16)
  );
  const luminance = (red * 299 + green * 587 + blue * 114) / 255000;

  return luminance < 0.52 ? 'dark' : 'light';
}

const storedTheme = localStorage.getItem(appThemeStorageKey);
const storedUiTheme = localStorage.getItem(uiThemeStorageKey);

export const defaultTheme = isThemeType(storedTheme)
  ? storedTheme
  : isThemeType(storedUiTheme)
    ? storedUiTheme
    : 'light';

localStorage.setItem(appThemeStorageKey, defaultTheme);
localStorage.setItem(uiThemeStorageKey, defaultTheme);

const storedMode = localStorage.getItem(panelThemeModeStorageKey);
const storedInterfaceStyle = localStorage.getItem(
  panelInterfaceStyleStorageKey
);
const defaultMode: PanelThemeMode = isPanelThemeMode(storedMode)
  ? storedMode
  : defaultTheme;
const defaultCustomColors = getStoredCustomColors();
const initialResolvedTheme =
  defaultMode === 'custom'
    ? getReadableTheme(defaultCustomColors.background)
    : defaultMode;

const initialState: ThemeState = {
  theme: initialResolvedTheme,
  mode: defaultMode,
  interfaceStyle: isPanelInterfaceStyle(storedInterfaceStyle)
    ? storedInterfaceStyle
    : 'simple',
  customColors: defaultCustomColors
};

export const setPanelInterfaceStyle = createAction(
  'theme/setPanelInterfaceStyle',
  (interfaceStyle: PanelInterfaceStyle) => {
    localStorage.setItem(panelInterfaceStyleStorageKey, interfaceStyle);
    return { payload: interfaceStyle };
  }
);

export const setTheme = createAction<PrepareAction<ThemeType>>(
  'theme/setTheme',
  (theme: ThemeType) => {
    localStorage.setItem(appThemeStorageKey, theme);
    localStorage.setItem(uiThemeStorageKey, theme);
    localStorage.setItem(panelThemeModeStorageKey, theme);
    return {
      payload: theme
    };
  }
);

export const setPanelTheme = createAction(
  'theme/setPanelTheme',
  (preferences: { mode: PanelThemeMode; customColors: PanelThemeColors }) => {
    const customColors = {
      ...defaultPanelThemeColors,
      ...preferences.customColors
    };
    const theme =
      preferences.mode === 'custom'
        ? getReadableTheme(customColors.background)
        : preferences.mode;

    localStorage.setItem(appThemeStorageKey, theme);
    localStorage.setItem(uiThemeStorageKey, theme);
    localStorage.setItem(panelThemeModeStorageKey, preferences.mode);
    localStorage.setItem(
      panelThemeColorsStorageKey,
      JSON.stringify(customColors)
    );

    return {
      payload: {
        theme,
        mode: preferences.mode,
        customColors
      }
    };
  }
);

export const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(setTheme, (state, action) => {
      state.theme = action.payload;
      state.mode = action.payload;
    });
    builder.addCase(setPanelTheme, (state, action) => {
      state.theme = action.payload.theme;
      state.mode = action.payload.mode;
      state.customColors = action.payload.customColors;
    });
    builder.addCase(setPanelInterfaceStyle, (state, action) => {
      state.interfaceStyle = action.payload;
    });
  }
});

export default themeSlice.reducer;
